import { useCallback, useEffect, useState } from "react";
import { useToast } from "../../../components/ui";
import { errorText } from "../../../lib/http";
import type { RequestStatus } from "../../auth";
import { getDashboard } from "../services/dashboardService";
import type { DashboardData } from "../types/dashboard.types";

/**
 * Everything the dashboard draws, in one hook.
 *
 * One call and one state, because the screen is one answer — see
 * `dashboardService`. The signup feed used to be filled from a short page of
 * the roster endpoint when the summary carried no feed of its own; the
 * dashboard route now sends both feeds, so that fallback is gone. It would
 * only fire now for an account list that is genuinely empty, where the honest
 * thing to show is the table saying so.
 *
 * A failure is said twice, in two registers. The toast is the event — it
 * carries the server's own reason where there is one and goes away by itself,
 * because a reader who has already seen it does not need to keep reading it.
 * What lasts is on the screen: dashes where the figures would be and a feed
 * offering the retry, so nothing on the page pretends to be a real zero.
 */
export function useDashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [status, setStatus] = useState<RequestStatus>("loading");
  const toast = useToast();

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      setData(await getDashboard());
      setStatus("ready");
    } catch (error) {
      console.error("dashboard failed to load:", error);
      setStatus("failed");
      toast.error(
        "Could not load the dashboard",
        errorText(
          error,
          "We could not reach the server. Check your connection and try again."
        )
      );
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, status, reload: load };
}

export default useDashboard;
